import { useEffect, useMemo, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatErr, CATEGORY } from "@/lib/api";

const DEPT_CATS = {
  service: ["equipment_service"],
  mgps: ["mgps_inspection", "mgps_installation"],
  sales: ["sales_visit"],
};
const EMPTY = { client_id: "", dept: "", category: "", assigned_to: "", task_title: "", due_date: "", description: "" };

export const QuickAssign = ({ onCreated }) => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false && u.role === "staff")));
  }, []);

  const engineers = useMemo(() => users.filter((u) => u.department === f.dept), [users, f.dept]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tasks", {
        task_title: f.task_title, category: f.category, client_id: f.client_id, assigned_to: f.assigned_to,
        description: f.description, due_date: new Date(f.due_date).toISOString(),
      });
      toast.success("Task assigned — engineer notified");
      setF(EMPTY);
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const ready = f.client_id && f.category && f.assigned_to && f.task_title.length >= 3 && f.due_date;

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quick-assign-form">
      <div>
        <Label className="text-xs">1. Client Hospital</Label>
        <Select value={f.client_id} onValueChange={set("client_id")}>
          <SelectTrigger data-testid="quick-assign-client-select"><SelectValue placeholder="Select hospital" /></SelectTrigger>
          <SelectContent className="bg-white">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.hospital_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">2. Department</Label>
          <Select value={f.dept} onValueChange={(v) => setF((p) => ({ ...p, dept: v, category: DEPT_CATS[v][0], assigned_to: "" }))}>
            <SelectTrigger data-testid="quick-assign-dept-select"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="service">Equipment Service</SelectItem>
              <SelectItem value="mgps">MGPS</SelectItem>
              <SelectItem value="sales">Sales</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Task Type</Label>
          <Select value={f.category} onValueChange={set("category")} disabled={!f.dept}>
            <SelectTrigger data-testid="quick-assign-category-select"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent className="bg-white">{(DEPT_CATS[f.dept] || []).map((c) => <SelectItem key={c} value={c}>{CATEGORY[c]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs">3. Engineer</Label>
        <Select value={f.assigned_to} onValueChange={set("assigned_to")} disabled={!f.dept}>
          <SelectTrigger data-testid="quick-assign-engineer-select"><SelectValue placeholder={f.dept && !engineers.length ? "No staff in this department" : "Pick engineer"} /></SelectTrigger>
          <SelectContent className="bg-white">{engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Task Title</Label>
        <Input value={f.task_title} onChange={(e) => set("task_title")(e.target.value)} placeholder="e.g. Annual MGPS Pressure Check" data-testid="quick-assign-title-input" />
      </div>
      <div>
        <Label className="text-xs">4. Due Date</Label>
        <Input type="datetime-local" value={f.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="quick-assign-due-input" />
      </div>
      <Button type="submit" disabled={!ready || busy} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="director-quick-assign-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />Assign Task</>}
      </Button>
    </form>
  );
};
